import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageWithPreview } from '@/components';
import { IContentBlockInstance } from '@/models/content-page.model';
import { SupportedLocales } from '@/models/localization.model';
import {
  getGalleryRowsFromBlockData,
  pickLocalizedLines,
  pickLocalizedString,
  pickProductCardPreviewPrices,
} from '../../helpers/blockPreviewHelpers';
import { hasRichHtmlTags, sanitizeRichText } from '../../helpers/richText';
import { resolveBlogMediaDisplayUrl } from '../../helpers/blogMedia';
import { productPrimaryImageUrl } from '../../helpers/productPreviewMedia';
import { categoryGalleryPreviewIds, useCategoriesByIdsForPreview } from '../../hooks/useCategoriesByIdsForPreview';
import { useProductsByIdsForPreview } from '../../hooks/useProductsByIdsForPreview';
import { getCategoryImage } from '@/modules/Categories/helpers/getCategoryImage';
import { MeasureUnitCode } from '@/modules/Products/helpers';
import {
  PreviewBanner,
  PreviewBannerBar,
  PreviewBannerCta,
  PreviewBody,
  PreviewBodyPlain,
  PreviewButtonPill,
  PreviewGalleryRow,
  PreviewGalleryThumb,
  PreviewHeading,
  PreviewListBlock,
  PreviewListIntro,
  PreviewListIntroPlain,
  PreviewListItems,
  PreviewPlaceholder,
  PreviewProductAccentStrip,
  PreviewProductCard,
  PreviewProductCardBody,
  PreviewProductImageWrap,
  PreviewProductImg,
  PreviewProductOldPrice,
  PreviewProductPrice,
  PreviewProductPriceRow,
  PreviewProductPriceUnit,
  PreviewProductSliderRow,
  PreviewProductTitle,
  PreviewShell,
} from './BlockCanvasPreviewStyles';

function renderLocaleTextPreview(
  raw: string,
  HtmlWrap: typeof PreviewBody,
  PlainWrap: typeof PreviewBodyPlain,
): JSX.Element | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (hasRichHtmlTags(trimmed)) {
    return <HtmlWrap dangerouslySetInnerHTML={{ __html: sanitizeRichText(trimmed) }} />;
  }
  return <PlainWrap>{raw}</PlainWrap>;
}

function renderListIntroPreview(
  raw: string,
  HtmlWrap: typeof PreviewListIntro,
  PlainWrap: typeof PreviewListIntroPlain,
): JSX.Element | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (hasRichHtmlTags(trimmed)) {
    return <HtmlWrap dangerouslySetInnerHTML={{ __html: sanitizeRichText(trimmed) }} />;
  }
  return <PlainWrap>{raw}</PlainWrap>;
}

const categoryGalleryItemWrapStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  width: '7.5rem',
  flexShrink: 0,
};

type TBlockCanvasPreview = {
  block: IContentBlockInstance;
  primaryLocale: SupportedLocales;
};

const ProductSliderPreview = ({ productIds }: { productIds: string[] }) => {
  const { t } = useTranslation(['contentPages', 'products']);
  const byId = useProductsByIdsForPreview(productIds);

  if (productIds.length === 0) {
    return <PreviewPlaceholder>{t('preview_skus_empty')}</PreviewPlaceholder>;
  }

  return (
    <PreviewProductSliderRow>
      {productIds.slice(0, 8).map((id) => {
        const p = byId.get(id);
        const img = p ? productPrimaryImageUrl(p) : undefined;
        const name = p?.name?.trim() || '—';
        const prices = p ? pickProductCardPreviewPrices(p) : null;
        const unitCode = p?.unit_code ?? MeasureUnitCode.PIECE;
        const unitShort = t(`product_unit_short_name_${unitCode}`, { ns: 'products', defaultValue: 'шт' });
        return (
          <PreviewProductCard key={id}>
            <PreviewProductImageWrap>
              <PreviewProductImg $src={img} title={name} />
            </PreviewProductImageWrap>
            <PreviewProductAccentStrip title={p?.sku ?? ''}>SKU: {p?.sku ?? '—'}</PreviewProductAccentStrip>
            <PreviewProductCardBody>
              <PreviewProductTitle title={name}>{name}</PreviewProductTitle>
              {prices ? (
                <PreviewProductPriceRow>
                  <span>
                    <PreviewProductPrice>{prices.current}</PreviewProductPrice>
                    <PreviewProductPriceUnit>грн/{unitShort}</PreviewProductPriceUnit>
                  </span>
                  {prices.old ? <PreviewProductOldPrice>{prices.old} грн</PreviewProductOldPrice> : null}
                </PreviewProductPriceRow>
              ) : null}
            </PreviewProductCardBody>
          </PreviewProductCard>
        );
      })}
    </PreviewProductSliderRow>
  );
};

const CategoryGalleryPreview = ({ categoryIds }: { categoryIds: string[] }) => {
  const { t } = useTranslation(['contentPages']);
  const displayIds = categoryGalleryPreviewIds(categoryIds);
  const byId = useCategoriesByIdsForPreview(categoryIds);

  if (displayIds.length === 0) {
    return <PreviewPlaceholder>{t('preview_categories_empty')}</PreviewPlaceholder>;
  }

  return (
    <PreviewGalleryRow>
      {displayIds.map((id) => {
        const c = byId.get(id);
        const name = c?.name?.trim() || '—';
        const src = getCategoryImage(c?.photo ?? '', null);
        return (
          <div key={id} style={categoryGalleryItemWrapStyle}>
            <PreviewGalleryThumb>
              <ImageWithPreview src={src} alt="" title={name} />
            </PreviewGalleryThumb>
            <PreviewProductTitle title={name}>{name}</PreviewProductTitle>
          </div>
        );
      })}
    </PreviewGalleryRow>
  );
};

const BlockCanvasPreview = ({ block, primaryLocale }: TBlockCanvasPreview) => {
  const { t } = useTranslation(['contentPages']);
  const d = block.data;

  if (!d || typeof d !== 'object') {
    return (
      <PreviewShell>
        <PreviewPlaceholder>{t('preview_empty')}</PreviewPlaceholder>
      </PreviewShell>
    );
  }

  const o = d as Record<string, unknown>;

  switch (block.type) {
    case 'big_banner': {
      const image = typeof o.image === 'string' ? resolveBlogMediaDisplayUrl(o.image) : '';
      const alt = pickLocalizedString(o.alt, primaryLocale);
      const btn = o.button as Record<string, unknown> | undefined;
      const btnText = btn ? pickLocalizedString(btn.text, primaryLocale) : '';
      const altStr = alt.trim();
      const btnStr = btnText.trim();
      return (
        <PreviewShell $bannerBleed>
          <PreviewBanner $src={image || undefined}>
            {btnStr ? <PreviewBannerCta $aboveAltBar={Boolean(altStr)}>{btnStr}</PreviewBannerCta> : null}
            {altStr ? <PreviewBannerBar>{altStr}</PreviewBannerBar> : null}
          </PreviewBanner>
        </PreviewShell>
      );
    }
    case 'text_with_heading': {
      const heading = pickLocalizedString(o.heading, primaryLocale);
      const text = pickLocalizedString(o.text, primaryLocale);
      const textPreview = renderLocaleTextPreview(text, PreviewBody, PreviewBodyPlain);
      return (
        <PreviewShell>
          {heading ? <PreviewHeading>{heading}</PreviewHeading> : null}
          {textPreview ?? <PreviewPlaceholder>{t('preview_text_empty')}</PreviewPlaceholder>}
        </PreviewShell>
      );
    }
    case 'list_with_heading_text': {
      const heading = pickLocalizedString(o.heading, primaryLocale);
      const intro = pickLocalizedString(o.text, primaryLocale);
      const introPreview = renderListIntroPreview(intro, PreviewListIntro, PreviewListIntroPlain);
      const lines = pickLocalizedLines(o.items, primaryLocale).slice(0, 4);
      return (
        <PreviewShell>
          {heading ? <PreviewHeading>{heading}</PreviewHeading> : null}
          <PreviewListBlock>
            {introPreview}
            {lines.length > 0 ? (
              <PreviewListItems>
                {lines.map((line, i) => (
                  <li key={i}>{line || '—'}</li>
                ))}
              </PreviewListItems>
            ) : (
              <PreviewPlaceholder>{t('preview_list_empty')}</PreviewPlaceholder>
            )}
          </PreviewListBlock>
        </PreviewShell>
      );
    }
    case 'text': {
      const text = pickLocalizedString(o.text, primaryLocale);
      const textPreview = renderLocaleTextPreview(text, PreviewBody, PreviewBodyPlain);
      return (
        <PreviewShell>{textPreview ?? <PreviewPlaceholder>{t('preview_text_empty')}</PreviewPlaceholder>}</PreviewShell>
      );
    }
    case 'button': {
      const text = pickLocalizedString(o.text, primaryLocale);
      const color = typeof o.color === 'string' ? o.color : undefined;
      return (
        <PreviewShell>
          {text ? (
            <PreviewButtonPill style={color ? { background: color } : undefined}>{text}</PreviewButtonPill>
          ) : (
            <PreviewPlaceholder>{t('preview_button_empty')}</PreviewPlaceholder>
          )}
        </PreviewShell>
      );
    }
    case 'product_slider': {
      const uuids = Array.isArray(o.uuids) ? (o.uuids as string[]) : [];
      const legacySkus = Array.isArray(o.skus) ? (o.skus as string[]) : [];
      const productIds = uuids.length > 0 ? uuids : legacySkus;
      return (
        <PreviewShell>
          <ProductSliderPreview productIds={productIds} />
        </PreviewShell>
      );
    }
    case 'category_gallery': {
      const ids = Array.isArray(o.category_ids) ? (o.category_ids as string[]) : [];
      return (
        <PreviewShell>
          <CategoryGalleryPreview categoryIds={ids} />
        </PreviewShell>
      );
    }
    case 'gallery': {
      const rows = getGalleryRowsFromBlockData(d);
      const header = pickLocalizedString(o.header, primaryLocale);
      return (
        <PreviewShell>
          {header.trim() ? <PreviewHeading>{header}</PreviewHeading> : null}
          {rows.length === 0 ? (
            <PreviewPlaceholder>{t('preview_gallery_empty')}</PreviewPlaceholder>
          ) : (
            <PreviewGalleryRow>
              {rows.map((row, i) => {
                if (!row || typeof row !== 'object') return null;
                const r = row as Record<string, unknown>;
                const rawSrc = typeof r.image === 'string' ? r.image : '';
                const src = resolveBlogMediaDisplayUrl(rawSrc);
                const alt = pickLocalizedString(r.alt, primaryLocale);
                return (
                  <PreviewGalleryThumb key={i}>
                    <ImageWithPreview src={src} alt={alt} title={t('preview_gallery_thumb')} />
                  </PreviewGalleryThumb>
                );
              })}
            </PreviewGalleryRow>
          )}
        </PreviewShell>
      );
    }
    default:
      return (
        <PreviewShell>
          <PreviewPlaceholder>{t('preview_generic')}</PreviewPlaceholder>
        </PreviewShell>
      );
  }
};

export default BlockCanvasPreview;
